/**
 * This is ReportAccordion which inherits from BaseAccordion
 * The main differences between all inherited accordions is applied styling and the header rendering.
 * This one is used in the report page for model runs.
 * It has
 * - Checkbox for section inclusion/exclusion
 * - Help tooltip with section-specific information
 * - Expand/collapse controls with RSuite icons
 */

import React, { useState, ReactNode, ComponentProps } from 'react'
import BaseAccordion from '../../../../ui_reusable/BaseAccordion'
import {
  AccordionContainer,
  PanelContainer,
  PanelHeader,
  PanelHeaderTitle,
  CustomHStack,
} from './styles/ReportAccordion.styles'
import { StandardCheckbox } from '../../../../ui_reusable/StandardInputs'
import theme from '../../../../../styles/theme'
import CustomIcons from '../../../../ui_reusable/CustomIcons'

interface ReportAccordionProps extends ComponentProps<typeof BaseAccordion> {
  children: ReactNode
  onContextReady?: () => void
}

const ReportAccordion: React.FC<ReportAccordionProps> & {
  Panel: typeof Panel
} = ({ children, onContextReady, ...props }) => {
  return (
    <AccordionContainer>
      <BaseAccordion {...props}>{children}</BaseAccordion>
    </AccordionContainer>
  )
}

interface PanelProps
  extends Omit<ComponentProps<typeof BaseAccordion.Panel>, 'renderHeader'> {
  /** Section title */
  header: string
  /** Optional help text shown in popover */
  helpText?: string
  /** Panel content */
  children: ReactNode
}

/**
 * Panel subcomponent for ReportAccordion
 */
const Panel: React.FC<PanelProps> = ({
  header,
  helpText,
  children,
  ...props
}) => {
  // State for section inclusion checkbox
  const [isChecked, setIsChecked] = useState(true)

  return (
    <PanelContainer>
      <BaseAccordion.Panel
        {...props}
        renderHeader={({ isActive, onToggle, noCollapse }) => (
          <PanelHeader>
            <PanelHeaderTitle onClick={onToggle}>{header}</PanelHeaderTitle>
            <CustomHStack>
              <StandardCheckbox
                size="small"
                color={theme.colors.fadedText}
                checked={isChecked}
                onChange={(event) => setIsChecked(event.target.checked)}
              />
              <CustomIcons.CircleQuestionThickIcon
                color={theme.colors.fadedText}
                size="s"
              />
              {!noCollapse &&
                (isActive ? (
                  <CustomIcons.ChevronUpIcon
                    size="xs"
                    color={theme.colors.fadedText}
                    onClick={onToggle}
                  />
                ) : (
                  <CustomIcons.ChevronDownIcon
                    size="xs"
                    color={theme.colors.fadedText}
                    onClick={onToggle}
                  />
                ))}
            </CustomHStack>
          </PanelHeader>
        )}
      >
        {children}
      </BaseAccordion.Panel>
    </PanelContainer>
  )
}

ReportAccordion.Panel = Panel

export default ReportAccordion
